import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useSettings, useSaveBatchPattern, useSaveReferenceWorkCenter, useBatchNumberPreview } from '../../api/settings';
import { useWorkCenters } from '../../api/referentiel';
import { ApiErrorMessage } from './ResourcesPanel';

/** Réglages généraux : nommage des fournées + opération de référence des OF. */
export default function GeneralPanel() {
  const { data: settings, isLoading } = useSettings();
  const { data: workCenters } = useWorkCenters();
  const save = useSaveBatchPattern();
  const saveRef = useSaveReferenceWorkCenter();
  const [pattern, setPattern] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings && !dirty) setPattern(settings.batchNumberPattern);
  }, [settings, dirty]);

  const preview = useBatchNumberPreview(pattern);

  if (isLoading) return <p className="text-gray-500">Chargement des réglages…</p>;

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">Nommage des fournées</h2>
      <p className="text-xs text-gray-500 mb-4">Modèle utilisé pour numéroter automatiquement les fournées à leur création.</p>

      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <label className="block">
          <span className="text-sm text-gray-600">Modèle</span>
          <input
            className="mt-1 w-full border rounded px-3 py-2 font-mono"
            value={pattern}
            onChange={(e) => { setPattern(e.target.value); setDirty(true); }}
            placeholder="F#####-{cycle}"
          />
        </label>

        <div className="text-sm">
          <span className="text-gray-500">Aperçu : </span>
          <span className="font-mono font-semibold text-blue-700">{preview.data?.example ?? '…'}</span>
          <span className="text-gray-400"> (cycle A42, aujourd'hui, n° 42)</span>
        </div>

        <div className="text-xs text-gray-500 bg-gray-50 border rounded p-3 space-y-1">
          <p className="font-medium text-gray-600">Jetons disponibles :</p>
          <ul className="grid grid-cols-2 gap-x-4">
            <li><code className="text-gray-700">#####</code> compteur (zéros selon le nombre de #)</li>
            <li><code className="text-gray-700">{'{cycle}'}</code> code du cycle</li>
            <li><code className="text-gray-700">AAAA</code> / <code className="text-gray-700">AA</code> année (4 ou 2 chiffres)</li>
            <li><code className="text-gray-700">MM</code> mois · <code className="text-gray-700">JJ</code> jour</li>
          </ul>
          <p>Tout autre caractère est repris tel quel. Sans compteur, une collision suffixe « -2 », « -3 »…</p>
        </div>

        <ApiErrorMessage error={save.error} />

        <div className="flex justify-end">
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            disabled={save.isPending || !pattern.trim() || pattern === settings?.batchNumberPattern}
            onClick={() => save.mutate(pattern, { onSuccess: () => setDirty(false) })}
          >
            <Save size={16} /> {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <h2 className="text-lg font-semibold text-gray-800 mb-1 mt-8">Avancement des OF</h2>
      <p className="text-xs text-gray-500 mb-4">
        Le statut et l'avancement d'un OF proviennent d'une opération de référence : une opération taggée
        <code className="mx-1 text-gray-700">reference</code> à l'import, sinon celle du centre de charge choisi ici,
        sinon l'opération la plus aval.
      </p>
      <div className="bg-white rounded-lg shadow p-4">
        <label className="block max-w-sm">
          <span className="text-sm text-gray-600">Centre de charge de référence (facultatif)</span>
          <select
            className="mt-1 w-full border rounded px-3 py-2 bg-white"
            value={settings?.referenceWorkCenter ?? ''}
            onChange={(e) => saveRef.mutate(e.target.value || null)}
          >
            <option value="">— aucun (repli sur l'opération la plus aval) —</option>
            {(workCenters ?? []).map((wc) => (
              <option key={wc.id} value={wc.code}>{wc.code} — {wc.label}</option>
            ))}
          </select>
        </label>
        <ApiErrorMessage error={saveRef.error} />
      </div>
    </div>
  );
}
